import englishMessages from './en'
import spanishMessages from './es'
import frenchMessages from './fr'
import japaneseMessages from './ja'
import chineseMessages from './zh'

type LocaleMessages = typeof englishMessages

/**
 * The five locale bundles registered in the language picker.
 *
 * ⚠️ Only `en` and `zh` are maintained (see CLAUDE.md). `fr`/`ja`/`es` are
 * partial — each carries roughly two thirds of en's keys — and they are NOT
 * opt-in: `detectBrowserLocale()` in App.tsx selects them straight from
 * `navigator.languages`, so a browser set to French lands on `fr` with no
 * user action.
 */
export const localeMessages: Record<string, LocaleMessages> = {
  en: englishMessages,
  zh: chineseMessages,
  fr: frenchMessages,
  ja: japaneseMessages,
  es: spanishMessages,
}

export const DEFAULT_LOCALE = 'en'

type Messages = Record<string, unknown>

/**
 * Deep-merge a partial locale over English so a key the locale is missing
 * resolves to the English phrase instead of leaking the raw dotted key.
 *
 * node-polyglot is constructed with ONLY the target locale's phrases and no
 * `allowMissing`/`onMissingKey` (ra-i18n-polyglot defaults `polyglotOptions`
 * to `{}`), so an absent key makes `translate()` return the key itself —
 * a vet on a French browser reads a literal `pos.dashboard.clinical.title`
 * as the dashboard heading. Merging is the fix that covers all 586 gaps at
 * once, including every key added to en in the future.
 *
 * Must be a DEEP merge: the bundles are nested objects, so a shallow spread
 * would let a locale's partial `pos` subtree shadow all of English's `pos`.
 */
const deepMergeMessages = (base: Messages, override: Messages): Messages => {
  const out: Messages = { ...base }
  for (const key of Object.keys(override)) {
    const o = override[key]
    const b = out[key]
    const bothPlainObjects =
      o != null &&
      typeof o === 'object' &&
      !Array.isArray(o) &&
      b != null &&
      typeof b === 'object' &&
      !Array.isArray(b)
    out[key] = bothPlainObjects
      ? deepMergeMessages(b as Messages, o as Messages)
      : (o as unknown as Messages[string])
  }
  return out
}

/**
 * Messages for a locale, with English as the fallback layer.
 *
 * An unknown locale yields plain English (the previous `?? englishMessages`
 * behaviour); a known but partial locale yields its own phrases layered over
 * English.
 */
export const resolveLocaleMessages = (locale: string): LocaleMessages => {
  const messages = localeMessages[locale]
  if (!messages || locale === DEFAULT_LOCALE) return englishMessages
  return deepMergeMessages(
    englishMessages as unknown as Messages,
    messages as unknown as Messages,
  ) as unknown as LocaleMessages
}
